import { FEATURE_FLAG_WILDCARD_ACCOUNT_KEY, FeatureFlag } from '../api/models';

/** updatedBy value the server records when a subsystem pauses itself. */
export const AUTO_PAUSE_UPDATED_BY = 'system:auto-pause';

/**
 * Which sort of account key a flag's account rows are keyed by. The two are not
 * interchangeable: a row keyed by the wrong one never matches the key the
 * subsystem resolves with, so it silently does nothing.
 */
export type FeatureFlagKeyKind = 'nc_username' | 'afternic_seller';

export interface FeatureFlagDescriptor {
  name: string;
  title: string;
  description: string;
  /**
   * 'action' flags let a subsystem act on the outside world and need their
   * global row on as well, so a global row that is off stops every account.
   * 'shadow' flags hold the subsystem to computing and logging while they are
   * on, and a global row that is on holds every account there whatever its own
   * row says — so enabling them is low-risk in both directions.
   */
  kind: 'action' | 'shadow';
  keyKind: FeatureFlagKeyKind;
}

export const keyKindLabels: Record<FeatureFlagKeyKind, { label: string; placeholder: string; help: string }> = {
  nc_username: {
    label: 'Namecheap username',
    placeholder: 'Namecheap username',
    help: 'This flag is keyed by Namecheap username, not by the Afternic seller the account maps to.',
  },
  afternic_seller: {
    label: 'Afternic seller',
    placeholder: 'Afternic seller',
    help: 'This flag is keyed by Afternic seller, not by the Namecheap username that maps to it. One seller can cover several Namecheap accounts.',
  },
};

export const featureFlagCatalog: FeatureFlagDescriptor[] = [
  {
    name: 'mail_router_enabled',
    title: 'Mail Router',
    description:
      'Auto-actions on inbound registrar mail. Classification and critical-mail forwarding run regardless of this flag — it gates the auto-actions only.',
    kind: 'action',
    keyKind: 'nc_username',
  },
  {
    name: 'mail_router_shadow',
    title: 'Mail Router Shadow',
    description:
      'Suppresses auto-actions only — classification, critical-mail forwarding, and alerts still run.',
    kind: 'shadow',
    keyKind: 'nc_username',
  },
  {
    name: 'contact_writes_enabled',
    title: 'Contact Writes',
    description: 'Registrant contact writes.',
    kind: 'action',
    keyKind: 'nc_username',
  },
  {
    name: 'contact_migration_shadow',
    title: 'Contact Migration Shadow',
    description: 'Compute and diff the contact write without sending it.',
    kind: 'shadow',
    keyKind: 'nc_username',
  },
  {
    name: 'raa_auto_verify_enabled',
    title: 'RAA Auto-Verify',
    description: 'Following the RAA verification link.',
    kind: 'action',
    keyKind: 'nc_username',
  },
  {
    name: 'afternic_resubmit_enabled',
    title: 'Afternic Resubmit',
    description: 'Re-listing to request Fast Transfer.',
    kind: 'action',
    keyKind: 'afternic_seller',
  },
  {
    name: 'fast_transfer_initiation_shadow',
    title: 'Fast Transfer Initiation Shadow',
    description: 'Build and log the relist payload without submitting.',
    kind: 'shadow',
    keyKind: 'afternic_seller',
  },
  {
    name: 'fast_transfer_clicker_enabled',
    title: 'Fast Transfer Clicker',
    description: 'Dispatching the browser sidecar to accept Fast Transfer terms.',
    kind: 'action',
    keyKind: 'afternic_seller',
  },
];

const catalogByName = new Map(featureFlagCatalog.map((d) => [d.name, d]));

export interface FeatureFlagGroup {
  name: string;
  descriptor?: FeatureFlagDescriptor;
  global?: FeatureFlag;
  /** The account-scoped row every account without one of its own inherits. */
  wildcard?: FeatureFlag;
  overrides: FeatureFlag[];
}

export function isWildcardRow(row: FeatureFlag): boolean {
  return row.scope === 'account' && row.accountKey === FEATURE_FLAG_WILDCARD_ACCOUNT_KEY;
}

/** The row an account with no row of its own resolves to. */
export interface UnlistedAccountDefault {
  source: 'wildcard' | 'global';
  row?: FeatureFlag;
  enabled: boolean;
}

export function defaultForUnlistedAccounts(group: FeatureFlagGroup): UnlistedAccountDefault {
  if (group.wildcard) {
    return { source: 'wildcard', row: group.wildcard, enabled: group.wildcard.enabled };
  }
  return { source: 'global', row: group.global, enabled: group.global?.enabled ?? false };
}

/**
 * What the accounts covered by a row resolve to once it is removed. An account
 * override hands its account back to the all-other-accounts default, falling
 * through to the global row only when there is no default row; the default row
 * itself hands every account it covered back to the global row.
 */
export function fallbackAfterRemoving(
  group: FeatureFlagGroup,
  row: FeatureFlag,
): UnlistedAccountDefault {
  if (isWildcardRow(row)) {
    return defaultForUnlistedAccounts({ ...group, wildcard: undefined });
  }
  return defaultForUnlistedAccounts(group);
}

/**
 * Groups flag rows by name in catalog order, with unknown names appended
 * alphabetically. Every catalog flag gets a group even when no row exists yet
 * (the PATCH endpoint upserts, so toggling creates the row).
 */
export function groupFeatureFlags(rows: FeatureFlag[]): FeatureFlagGroup[] {
  const byName = new Map<string, FeatureFlagGroup>();
  for (const descriptor of featureFlagCatalog) {
    byName.set(descriptor.name, { name: descriptor.name, descriptor, overrides: [] });
  }
  for (const row of rows) {
    let group = byName.get(row.name);
    if (!group) {
      group = { name: row.name, descriptor: catalogByName.get(row.name), overrides: [] };
      byName.set(row.name, group);
    }
    if (isWildcardRow(row)) {
      group.wildcard = row;
    } else if (row.scope === 'account' && row.accountKey !== '') {
      group.overrides.push(row);
    } else {
      group.global = row;
    }
  }
  const groups = Array.from(byName.values());
  for (const group of groups) {
    group.overrides.sort((a, b) => a.accountKey.localeCompare(b.accountKey));
  }
  const catalogOrder = new Map(featureFlagCatalog.map((d, i) => [d.name, i]));
  groups.sort((a, b) => {
    const ai = catalogOrder.get(a.name);
    const bi = catalogOrder.get(b.name);
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
  return groups;
}

/** Replaces the row with the same (name, scope, accountKey), or appends it. */
export function upsertFlagRow(rows: FeatureFlag[], row: FeatureFlag): FeatureFlag[] {
  const idx = rows.findIndex(
    (r) => r.name === row.name && r.scope === row.scope && r.accountKey === row.accountKey,
  );
  if (idx === -1) return [...rows, row];
  const next = rows.slice();
  next[idx] = row;
  return next;
}

export function removeOverrideRow(rows: FeatureFlag[], name: string, accountKey: string): FeatureFlag[] {
  return rows.filter(
    (r) => !(r.name === name && r.scope === 'account' && r.accountKey === accountKey),
  );
}

/** True when the row was switched off by an auto-pause trigger rather than a person. */
export function isSelfPaused(row: FeatureFlag): boolean {
  return !row.enabled && row.updatedBy === AUTO_PAUSE_UPDATED_BY;
}

/**
 * Unknown flags are treated as action-capable so enabling them still asks for
 * confirmation.
 */
export function isActionCapable(name: string): boolean {
  return catalogByName.get(name)?.kind !== 'shadow';
}
